import { useState } from "react";
import { api, save } from "./api";
import type { EventData } from "./types";
type Preview = {
  name: string;
  photos: number;
  assets: number;
  sharedAssets: string[];
  synced: boolean;
};
export function EventCleanup({
  event,
  onDeleted,
  onCleared,
  notify,
}: {
  event: EventData;
  onDeleted: () => void;
  onCleared: () => Promise<void>;
  notify: (message: string) => void;
}) {
  const [action, setAction] = useState<"delete" | "clear" | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function open(next: "delete" | "clear") {
    setBusy(true);
    setError("");
    try {
      setPreview(await api(`/admin/events/${event.id}/cleanup`));
      setConfirmation("");
      setAction(next);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function confirm() {
    setBusy(true);
    setError("");
    try {
      const result = await save(
        `/admin/events/${event.id}${action === "clear" ? "/clear-photos" : ""}`,
        { confirmName: confirmation },
        action === "clear" ? "POST" : "DELETE",
      );
      notify(
        result.cleanupPending
          ? "Event updated. Some managed files could not be removed; cleanup will retry when the server restarts."
          : action === "delete"
            ? "Event and its managed copies deleted."
            : "Photos cleared. Watched folder and Drive imports disconnected.",
      );
      setAction(null);
      if (action === "delete") onDeleted();
      else await onCleared();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="panel event-cleanup">
      <p className="eyebrow">SUPER ADMIN ONLY</p>
      <h2>Event cleanup</h2>
      <p>
        Remove Photo Wall’s managed copies on this server. Original source
        folders and Google Drive files stay untouched.
      </p>
      <div className="button-row">
        <button disabled={busy} onClick={() => void open("clear")}>
          Clear photos…
        </button>
        <button disabled={busy} onClick={() => void open("delete")}>
          Delete event…
        </button>
      </div>
      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}
      {action && preview && (
        <div
          className="cleanup-confirm"
          role="dialog"
          aria-label={
            action === "delete"
              ? "Confirm event deletion"
              : "Confirm clearing photos"
          }
        >
          <h3>
            {action === "delete"
              ? "Delete this event permanently?"
              : "Clear this event’s photos?"}
          </h3>
          <p>
            {preview.photos} photos (original copies and display previews)
            {action === "delete" ? ` and ${preview.assets} design assets` : ""}{" "}
            will be removed from this server. This cannot be undone.
          </p>
          <p>
            {action === "delete"
              ? "The event account and all local event links will stop working."
              : "Event details and designs remain. Folder watching and Drive imports will be disconnected to prevent re-import; you can reconnect them later."}
          </p>
          <p>
            Copies on other installations, offline browser caches and guests’
            downloads are not erased by this action. Connected display and
            gallery pages refresh automatically.
          </p>
          {preview.synced && action === "delete" && (
            <p>
              This installation will stop downloading this event from the
              central server. The central copy remains.
            </p>
          )}
          {action === "delete" && preview.sharedAssets.length > 0 && (
            <div className="warning">
              Design assets are still used by {preview.sharedAssets.join(", ")}.
              Replace those references before deleting, or clear photos instead.
            </div>
          )}
          <label>
            Type “{preview.name}” to confirm
            <input
              autoComplete="off"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              disabled={busy}
            />
          </label>
          <div className="button-row">
            <button disabled={busy} onClick={() => setAction(null)}>
              Cancel
            </button>
            <button
              className="danger"
              disabled={
                busy ||
                confirmation !== preview.name ||
                (action === "delete" && preview.sharedAssets.length > 0)
              }
              onClick={() => void confirm()}
            >
              {busy
                ? "Cleaning up…"
                : action === "delete"
                  ? "Permanently delete event"
                  : "Permanently clear photos"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
